import React from 'react';
import { UseFormReturn, FieldValues, FormProvider } from 'react-hook-form';
import { EditableGroupProps, InputProps, EditableGroup } from '@cad-arc';
import {
    IntegratedFormContext,
    GetGenericButtonProps,
    GetInputProps,
    GetSaveButtonProps,
    IntegratedFormMutation,
    IntegratedFormOnSubmit,
    IntegratedFormQuery,
    GetFormInputButtonProps,
    IntegratedForm,
} from './integrated-form-context';
import { InputHandlerManager } from './input-handle-manager';
import { useInputSizeForApplicationType } from '../common/hooks/use-input-size-for-application-type';

type FormStateProviderBaseProps<T> = {
    query?: IntegratedFormQuery;
    form: UseFormReturn<T>;
    children: React.ReactNode;
    withEditable?: true;
    editableMode?: EditableGroupProps['mode'];
    inputSize?: InputProps['size'];
    mode?: 'read-only';
    keepInputsEnabledOnMutationLoading?: true;
    fuzzyMatchingEnabled?: boolean;
};

type FormStateProviderProps<T extends FieldValues> = FormStateProviderBaseProps<T> &
    (
        | {
              onSubmit: IntegratedFormOnSubmit<T>;
              mutation: IntegratedFormMutation;
          }
        | {
              mutation: IntegratedFormMutation<T>;
          }
    );

export function IntegratedFormProvider<T>(props: FormStateProviderProps<T>): React.ReactElement {
    const query: IntegratedFormQuery = React.useMemo(() => {
        return (
            props.query ?? {
                isInitialLoading: false,
                isError: false,
            }
        );
    }, [props.query]);
    const inputHandleManager = React.useState(new InputHandlerManager())[0];
    const isSubmitting = React.useRef<boolean>(false);
    const inputSize = useInputSizeForApplicationType(props.inputSize);

    const returnsMemo: IntegratedForm<T> = React.useMemo(() => {
        const getInputProps: GetInputProps = (args?: { isDisabled?: boolean }) => {
            if (props.keepInputsEnabledOnMutationLoading) {
                return {
                    isDisabled: query.isError || !!args?.isDisabled,
                };
            }
            return { isDisabled: query.isError || props.mutation.isLoading || !!args?.isDisabled };
        };
        const getGenericButtonProps: GetGenericButtonProps = (args?: { isDisabled?: boolean }) => ({
            isDisabled: props.mutation.isLoading || !!args?.isDisabled,
        });
        const getCancelButtonProps: GetGenericButtonProps = (args?: { isDisabled?: boolean }) => ({
            isDisabled: props.mutation.isLoading || !!args?.isDisabled,
        });
        const getSaveButtonProps: GetSaveButtonProps = (args?: {
            isDisabled?: boolean;
            isLoading?: boolean;
        }) => ({
            isLoading: props.mutation.isLoading || !!args?.isLoading,
            isDisabled: query.isInitialLoading || query.isError || !!args?.isDisabled,
        });

        const getFormInputButtonProps: GetFormInputButtonProps = (args?: {
            isDisabled?: boolean;
        }) => {
            return {
                isDisabled: props.mode === 'read-only' || args?.isDisabled,
            };
        };
        const defaultOnSubmit = (formValue: T): void => {
            props.mutation.mutate(formValue);
        };
        const wrappedOnSubmit = (
            e: React.BaseSyntheticEvent<object, unknown, unknown> | undefined,
            onSuccess?: () => void,
            onError?: (err: unknown) => void
        ): void => {
            if (isSubmitting.current === true) {
                return;
            }
            isSubmitting.current = true;
            props.form.handleSubmit(
                async (formValues) => {
                    try {
                        if ('onSubmit' in props) {
                            try {
                                await props.onSubmit(formValues);
                                onSuccess?.();
                            } catch (e) {
                                onError?.(e);
                            }
                        } else {
                            defaultOnSubmit(formValues);
                        }
                    } finally {
                        isSubmitting.current = false;
                    }
                },
                () => {
                    isSubmitting.current = false;
                }
            )(e);
        };
        return {
            inputSize,
            form: props.form,
            getCancelButtonProps,
            getSaveButtonProps,
            getGenericButtonProps,
            getFormInputButtonProps,
            getInputProps,
            query,
            mutation: props.mutation,
            onSubmit: wrappedOnSubmit,
            withEditable: props.withEditable,
            inputHandleManager,
            mode: props.mode,
            fuzzyMatchingEnabled: props.fuzzyMatchingEnabled,
            isSubmitting: isSubmitting.current,
        };
    }, [props, query, inputHandleManager, inputSize]);

    return (
        <FormProvider {...props.form}>
            {props.withEditable ? (
                <IntegratedFormContext.Provider value={returnsMemo}>
                    <EditableGroup mode={props.editableMode}>{props.children}</EditableGroup>
                </IntegratedFormContext.Provider>
            ) : (
                <IntegratedFormContext.Provider value={returnsMemo}>
                    {props.children}
                </IntegratedFormContext.Provider>
            )}
        </FormProvider>
    );
}
